import { useMemo } from "react";
import { LandCard } from "./LandCard";
import { LandFilters } from "./LandFilters";
import { CardSkeleton, EmptyState, ErrorState, SectionHeader } from "./primitives";
import { useListings, useMarketplaceConfig, useListingsZones, listingCells } from "./useMarketplace";
import { slotCount, type ZoneKey } from "./mkt-format";
import { goMarketplace, patchMarketplaceQuery, type MarketplaceRouteState } from "./mkt-routing";
import { useT } from "../../i18n";
import type { Listing } from "../../lib/marketplace-api";

const ONE_USDC_MICRO = 1_000_000;

function applyFilters(
  listings: Listing[],
  zones: Map<string, ZoneKey>,
  q: Record<string, string>,
): Listing[] {
  let out = [...listings];
  if (q.zone) out = out.filter((l) => zones.get(l.order_hash) === q.zone);
  const min = q.min ? Math.round(Number(q.min) * ONE_USDC_MICRO) : null;
  const max = q.max ? Math.round(Number(q.max) * ONE_USDC_MICRO) : null;
  if (min != null && !Number.isNaN(min)) out = out.filter((l) => l.price_micro >= min);
  if (max != null && !Number.isNaN(max)) out = out.filter((l) => l.price_micro <= max);
  const minslots = q.minslots ? Number(q.minslots) : null;
  if (minslots != null && !Number.isNaN(minslots)) out = out.filter((l) => slotCount(listingCells(l)) >= minslots);
  switch (q.sort) {
    case "price_asc":
      out.sort((a, b) => a.price_micro - b.price_micro);
      break;
    case "price_desc":
      out.sort((a, b) => b.price_micro - a.price_micro);
      break;
    case "slots_desc":
      out.sort((a, b) => slotCount(listingCells(b)) - slotCount(listingCells(a)));
      break;
    default:
      break; // "recent" — server already returns created_at desc
  }
  return out;
}

export function LandTab({ route }: { route: MarketplaceRouteState }) {
  const t = useT();
  const { data: config } = useMarketplaceConfig();
  const listingsQuery = useListings("active");
  const listings = useMemo(() => listingsQuery.data?.listings ?? [], [listingsQuery.data]);
  const zones = useListingsZones(listings);
  const filtered = useMemo(() => applyFilters(listings, zones, route.query), [listings, zones, route.query]);

  return (
    <div data-testid="marketplace-land-tab" className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
      <aside className="lg:sticky lg:top-4 h-fit">
        <LandFilters route={route} onPatch={(patch) => patchMarketplaceQuery(route, patch)} />
      </aside>

      <div>
        <SectionHeader
          index="01"
          title={t("marketplace.land.sectionTitle")}
          testId="land-section-header"
          action={
            <span className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-text-muted">
              {filtered.length} {t("marketplace.states.active")}
            </span>
          }
        />

        {listingsQuery.isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
          </div>
        )}

        {listingsQuery.isError && (
          <ErrorState
            title={t("marketplace.land.loadErrorTitle")}
            detail={t("marketplace.land.loadErrorDetail")}
            onRetry={() => listingsQuery.refetch()}
            testId="land-error"
          />
        )}

        {!listingsQuery.isLoading && !listingsQuery.isError && filtered.length === 0 && (
          <EmptyState
            title={listings.length === 0 ? t("marketplace.land.emptyNoneTitle") : t("marketplace.land.emptyFilteredTitle")}
            hint={listings.length === 0 ? t("marketplace.land.emptyNoneHint") : t("marketplace.land.emptyFilteredHint")}
            testId="land-empty"
          />
        )}

        {filtered.length > 0 && (
          <div data-testid="land-grid" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((l) => (
              <LandCard
                key={l.order_hash}
                listing={l}
                zone={zones.get(l.order_hash) ?? "unknown"}
                config={config}
                onOpen={() => goMarketplace("land", route.query, { kind: "land", id: l.order_hash })}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
